import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ACCENT, Field, PrimaryButton, SecondaryButton, inputClass, ringStyle } from "../artistStudio/shared";
import EvidenceUploader from "./EvidenceUploader";
import { claimErrorText, fieldErrors, formatDay, rightsClaimApi } from "./rightsClaimApi";

export const REASON_MIN = 20;
export const REASON_MAX = 1000;
export const SHARE_MIN = 1;
export const SHARE_MAX = 99;

// The messages to show before anything is sent (FSD 6.8.3): a dispute needs a reason of 20 to 1,000 characters; accepting needs the
// outcome, and a revenue share needs a whole percent from 1 to 99. Returns { field: message }; empty means it can be sent.
export function responseErrors(form) {
  const errors = {};
  if (!form.decision) errors.decision = "Choose Dispute claim or Accept claim.";
  if (form.decision === "dispute") {
    const length = form.reason.trim().length;
    if (!length) errors.reason = "Explain why you dispute this claim.";
    else if (length < REASON_MIN) errors.reason = `Your reason must be at least ${REASON_MIN} characters (${length}/${REASON_MIN}).`;
    else if (length > REASON_MAX) errors.reason = `Your reason can be at most ${REASON_MAX.toLocaleString()} characters.`;
  }
  if (form.decision === "accept") {
    if (!form.outcome) errors.outcome = "Choose revenue share or takedown.";
    if (form.outcome === "revenue_share") {
      const text = String(form.share).trim();
      const n = Number(text);
      if (!text) errors.share_percent = "Enter the revenue share.";
      else if (!Number.isInteger(n)) errors.share_percent = "Enter the revenue share as a whole number.";
      else if (n < SHARE_MIN || n > SHARE_MAX) errors.share_percent = `The revenue share must be from ${SHARE_MIN}% to ${SHARE_MAX}%.`;
    }
  }
  return errors;
}

// The request body for the form, or null when it is not valid.
export function responseBody(form) {
  if (Object.keys(responseErrors(form)).length) return null;
  if (form.decision === "dispute") return { decision: "dispute", reason: form.reason.trim(), evidence: form.evidence };
  if (form.outcome === "takedown") return { decision: "accept", outcome: "takedown" };
  return { decision: "accept", outcome: "revenue_share", share_percent: Number(form.share) };
}

// "Respond to claim" (Artist Studio > Rights & Legal > Claims Received). One answer per claim: dispute it with a reason and evidence,
// or accept it and choose the outcome, a revenue share or a takedown. The track stays live while the claim is open.
export default function ClaimResponseDialog({ claim, onClose, onDone }) {
  const [details, setDetails] = useState("");
  const [form, setForm] = useState({ decision: "", reason: "", evidence: [], outcome: "", share: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");

  useEffect(() => {
    let live = true;
    rightsClaimApi.detail(claim.id).then((c) => live && setDetails(c?.description || ""), () => {});
    return () => { live = false; };
  }, [claim.id]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const submit = async () => {
    const local = responseErrors(form);
    setErrors(local);
    setFailure("");
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      await rightsClaimApi.respond(claim.id, responseBody(form));
      toast.success("Your response was sent.");
      onDone();
    } catch (e) {
      const fields = fieldErrors(e);
      if (Object.keys(fields).length) setErrors(fields);
      else setFailure(claimErrorText(e, "We couldn't send your response. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const radio = (name, value, label, hint) => (
    <label className="flex items-start gap-2 text-sm text-gray-800">
      <input type="radio" name={name} className="mt-1" checked={form[name] === value} onChange={() => set({ [name]: value })} style={{ accentColor: ACCENT }} data-testid={`respond-${name}-${value}`} />
      <span><span className="font-semibold">{label}</span>{hint && <span className="block text-gray-500">{hint}</span>}</span>
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex [align-items:safe_center] justify-center overflow-y-auto bg-black/40 px-4 py-4" role="dialog" aria-modal="true" aria-labelledby="respond-title" data-testid="respond-dialog">
      <div className="my-8 w-full max-w-xl rounded-3xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <h2 id="respond-title" className="text-lg font-bold text-gray-900">Respond to claim</h2>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close" className="text-2xl leading-none text-gray-500">×</button>
        </div>
        <dl className="mt-3 space-y-1 text-sm">
          <div className="flex gap-2"><dt className="w-24 text-gray-500">Track</dt><dd className="font-semibold text-gray-900">{claim.track?.title || `#${claim.track?.id}`}</dd></div>
          <div className="flex gap-2"><dt className="w-24 text-gray-500">Claimant</dt><dd className="text-gray-900">{claim.claimant_name || "—"}</dd></div>
          <div className="flex gap-2"><dt className="w-24 text-gray-500">Received</dt><dd className="text-gray-900">{formatDay(claim.received_at) || "—"}</dd></div>
        </dl>
        {details && <p className="mt-3 whitespace-pre-wrap rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-700" data-testid="respond-details">{details}</p>}
        {claim.due_at && <p className="mt-3 text-sm font-semibold text-amber-800" data-testid="respond-deadline">Respond by {formatDay(claim.due_at)}</p>}
        <p className="mt-1 text-xs text-gray-500">Your track stays live while the claim is open, and its earnings are held until the claim is closed. You can answer once.</p>

        <div className="mt-4 space-y-3">
          {radio("decision", "dispute", "Dispute claim", "You believe the claim is wrong. Our rights team will decide.")}
          {radio("decision", "accept", "Accept claim", "Choose what you offer the claimant.")}
          {errors.decision && <p className="text-sm text-red-600">{errors.decision}</p>}
        </div>

        {form.decision === "dispute" && (
          <div className="mt-4 space-y-4" data-testid="respond-dispute">
            <Field label="Why do you dispute this claim?" required error={errors.reason} hint={`${form.reason.trim().length}/${REASON_MAX}`}>
              <textarea value={form.reason} onChange={(e) => set({ reason: e.target.value })} rows={4} className={inputClass(!!errors.reason)} style={ringStyle} data-testid="respond-reason" />
            </Field>
            <Field label="Evidence (optional)" error={errors.evidence}>
              <EvidenceUploader value={form.evidence} onChange={(evidence) => set({ evidence })} disabled={busy} maxFiles={5} />
            </Field>
          </div>
        )}

        {form.decision === "accept" && (
          <div className="mt-4 space-y-3 rounded-2xl border border-gray-200 p-4" data-testid="respond-accept">
            {radio("outcome", "revenue_share", "Revenue share", "The claimant receives a share of this track's future earnings, paid automatically into their wallet.")}
            {form.outcome === "revenue_share" && (
              <Field label="Claimant's share (%)" required error={errors.share_percent}>
                <input inputMode="numeric" value={form.share} onChange={(e) => set({ share: e.target.value.replace(/[^0-9]/g, "").slice(0, 2) })} className={inputClass(!!errors.share_percent)} style={ringStyle} data-testid="respond-share" />
              </Field>
            )}
            {radio("outcome", "takedown", "Takedown", "Your track is taken down from Pipaan.")}
            {errors.outcome && <p className="text-sm text-red-600">{errors.outcome}</p>}
          </div>
        )}

        {failure && <p className="mt-3 text-sm text-red-600" role="alert" data-testid="respond-failure">{failure}</p>}
        <div className="mt-5 flex flex-wrap justify-end gap-3">
          <SecondaryButton onClick={onClose} disabled={busy} data-testid="respond-cancel">Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={busy} data-testid="respond-submit">{busy ? "Sending…" : "Submit Response"}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}
